import {useEffect, useRef, useState} from 'react';

import casusBackend, {
  CasusSearchItem,
  PageItemCasusSearchItem,
} from '@lexian/api/casus-api';
import {SearchCasusFilters} from '_/types/casus';
import {
  handlePaginatedLoadableFetch,
  usePaginatedItemLoader,
} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PaginatedLoadable,
  PaginationArgumentsWithoutCursor,
  RequiredDataLoadable,
  State,
} from '_/utilities/types';

const defaultSearchCasusFilters: SearchCasusFilters = {
  lockedFilter: 'ALL',
  stateFilters: ['ACTIVE', 'INACTIVE', 'ARCHIVED'],
  type: 'ALL',
  protectedFilter: 'ALL',
  invoiceableFilter: 'ALL',
  exactSearch: false,
};

export const activeCasusFilters: SearchCasusFilters = {
  ...defaultSearchCasusFilters,
  stateFilters: ['ACTIVE'],
};

export const assignableCasusFilters: SearchCasusFilters = {
  ...activeCasusFilters,
  lockedFilter: 'UNLOCKED',
};

export function searchCasus(
  filters: SearchCasusFilters = defaultSearchCasusFilters
) {
  return (
    paginationArguments: PaginationArgumentsWithoutCursor = defaultPaginationArguments,
    currentLoadable: PaginatedLoadable<PageItemCasusSearchItem[]>,
    {searchQuery, ids}: {searchQuery: string; ids?: string[]}
  ): Promise<PaginatedLoadable<PageItemCasusSearchItem[]>> =>
    handlePaginatedLoadableFetch(
      {
        endpointFunction: ({parameters, ...rest}) =>
          casusBackend.searchAllCasus({
            parameters: {
              ...filters,
              ...parameters,
            },
            ...rest,
          }),
        otherParams: {query: searchQuery, ids},
        getPageItemFromResponse: (body) => body.casus,
      },
      paginationArguments,
      currentLoadable
    );
}

export const searchAssignableCasus = searchCasus(assignableCasusFilters);

export function useCasusByIds(
  casusIds: string[],
  filters: SearchCasusFilters = defaultSearchCasusFilters
): RequiredDataLoadable<CasusSearchItem[]> {
  const {fetchIds} = usePaginatedItemLoader(searchCasus(filters));
  const [casus, setCasus] = useState<RequiredDataLoadable<CasusSearchItem[]>>({
    state: State.Loading,
    data: [],
  });

  const casusIdsRef = useRef(casusIds);
  casusIdsRef.current = casusIds;
  const casusIdsKey = casusIds.join();
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    const ids = casusIdsRef.current;
    if (ids.length === 0) {
      setCasus({state: State.Success, data: []});
      return undefined;
    }

    let unmounted = false;
    setCasus({state: State.Loading, data: []});

    (async (): Promise<void> => {
      const loaded = await fetchIds(ids);
      if (unmounted) {
        return;
      }
      const casusById = new Map(
        loaded.data?.map(({item}) => [item.id, item]) ?? []
      );
      setCasus({
        state: loaded.state,
        data: ids.flatMap((id) => casusById.get(id) ?? []),
      });
    })();

    return () => {
      unmounted = true;
    };
  }, [casusIdsKey, filtersKey, fetchIds]);

  return casus;
}
